import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import styles from "./Reporting.module.scss";
import { Grid } from "@mui/material";
import { withTranslation } from "react-i18next";
import SubNav from "../../shared/subnav/SubNav";
import { base64FileDownload, fileDownload } from "../../utils/FileDownloadUtil";
import { logMsg } from "../../common/Logger";
import { InfoIconWithMessage } from "../../shared/infoIcon/InfoIcon";
import {
  CA_BRONCO_CATALOG_ID,
  CA_BRONCO_SPORT_CATALOG_ID,
  CA_F150_CATALOG_ID,
  CANADA_FRENCH,
} from "../../common/Constants";
import NscReportingClient from "../../clients/NscReportingClient";
import DealerReportingClient from "../../clients/DealerReportingClient";
import {
  IS_CANADA_MARKET,
  IS_FORD_BRAND,
  NOTNULL_OR_NOTUNDEFINED,
} from "../../utils/EmpUtil";
import UpperCaseText from "../../utils/UpperCaseText";
import BrandSettingsClient from "../../clients/BrandSettingsClient";
import ReportFilter from "./ReportFilter";
import UiDateUtils from "../../utils/UiDateUtils";
import EMPButton from "../../shared/components/EMPButton/EMPButton";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import VehicleLinesClient from "../../clients/VehicleLinesClient";

const Reporting = (props) => {
  const [displayFilters, setDisplayFilters] = useState(false);
  const [reports, setReports] = useState([]);
  const [dateRangeMethod, setDateRangeMethod] = useState("auto");
  const [dateRange, setDateRange] = useState(
    props.t("fieldReporting.dropDowns.last30Days")
  );
  const [dateRanges] = useState([
    props.t("fieldReporting.dropDowns.last30Days"),
    props.t("fieldReporting.dropDowns.last90Days"),
    props.t("fieldReporting.dropDowns.yearToDate"),
  ]);
  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const [vehicleLines, setVehicleLines] = useState(null);

  const getTodayDate = () => {
    const today = new Date();
    return `${today.getMonth() + 1}-${today.getDate()}-${today.getFullYear()}`;
  };

  const redirectToAccessoryPricingSearch = () => {
    props.history.push({
      pathname: `/pricing/accessories`,
      state: { redirectToAccSearch: true },
    });
  };

  const downloadDealerReservationReport = (report) => {
    const reqData = {
      dateRange: dateRangeMethod === "manual" ? "" : dateRange,
      dateFrom:
        dateRangeMethod === "manual"
          ? UiDateUtils.formatDateMmDdYyyy(fromDate)
          : "",
      dateTo:
        dateRangeMethod === "manual"
          ? UiDateUtils.formatDateMmDdYyyy(toDate)
          : "",
    };
    const dealerGroup =
      IS_CANADA_MARKET(props.user.market) &&
      props.user?.dealerGroup?.filter(
        (d) => props.user.commonId === d.commonId
      );
    const lang =
      dealerGroup?.length > 0 &&
      dealerGroup?.[0]?.language?.toUpperCase() === "F"
        ? CANADA_FRENCH
        : props.user.lang;

    DealerReportingClient.downloadDealerReservationReportInCsv(
      props.user,
      lang,
      "",
      true,
      reqData,
      UpperCaseText(props.user.userType) === "FIELD_VIEW"
    )
      .then((res) => {
        const filename =
          props.t(report.name) + " - " + getTodayDate() + ".xlsx";
        const BOM = "\uFEFF";
        base64FileDownload(
          decodeURIComponent(BOM + res),
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          filename
        );
      })
      .catch((reason) => {
        logMsg("reason", reason);
      });
  };

  const downloadDealerActivationReport = (report) => {
    props.hideOrShow(true);
    NscReportingClient.downloadDealerActivationReportInCsv(props.user)
      .then((res) => {
        const filename = props.t(report.name) + ".csv";
        const BOM = "\uFEFF";
        fileDownload(
          decodeURIComponent(BOM + res),
          "text/csv;charset=utf-8;",
          filename
        );
      })
      .catch((reason) => {
        logMsg("reason", reason);
      })
      .finally(() => {
        props.hideOrShow(false);
      });
  };

  const downloadDealerReservationSummary = async (report) => {
    props.hideOrShow(true);
    try {
      let currentVehicleLines = vehicleLines;
      if (!currentVehicleLines) {
        currentVehicleLines =
          await VehicleLinesClient.getVehicleLinesByCatalogs(props.user);
        setVehicleLines(currentVehicleLines);
      }
      const res = await NscReportingClient.downloadDealerReservationSummary(
        props.user,
        props.user.market,
        report.catalog,
        currentVehicleLines
      );
      const filename = `${props.t(report.name)}.xlsx`;
      const BOM = "\uFEFF";
      base64FileDownload(
        decodeURIComponent(BOM + res),
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        filename
      );
    } catch (error) {
      logMsg("reason", error);
    } finally {
      props.hideOrShow(false);
    }
  };

  const getReports = () => {
    const commonReport = [
      {
        name: "Reporting.report1Title",
        key: "report1",
        downloadLink: downloadDealerActivationReport,
      },
    ];

    if (props.user.market.toLowerCase() === "can") {
      if (props.userDetails?.roles?.includes("EMP_ADMIN")) {
        return {
          displayFilters: false,
          reports: [
            ...commonReport,
            {
              name: "Reporting.report4Title",
              downLoadName: "Reporting.downloadDealerReportBronco",
              key: "report2",
              catalog: CA_BRONCO_CATALOG_ID,
              downloadLink: downloadDealerReservationSummary,
            },
            {
              name: "Reporting.report5Title",
              downLoadName: "Reporting.downloadDealerReportBroncoSport",
              key: "report3",
              catalog: CA_BRONCO_SPORT_CATALOG_ID,
              downloadLink: downloadDealerReservationSummary,
            },
            {
              name: "Reporting.report7Title",
              downLoadName: "Reporting.downloadDealerReportF150",
              key: "report4",
              catalog: CA_F150_CATALOG_ID,
              downloadLink: downloadDealerReservationSummary,
            },
          ],
        };
      }
      if (
        props.userDetails?.roles?.length === 1 &&
        props.userDetails?.roles?.[0] === "EMP_ECOMM_ACCESSORIES"
      ) {
        return { displayFilters: false, reports: [] };
      }
      return {
        displayFilters: UpperCaseText(props.user.market) === "CAN",
        reports: [
          {
            name: "Reservations",
            downLoadName: "Reservations",
            key: "report4",
            downloadLink: downloadDealerReservationReport,
          },
        ],
      };
    }

    return {
      displayFilters: false,
      reports: [
        ...commonReport,
        {
          name: "Reporting.report2Title",
          downLoadName: "Reporting.downloadDealerReport",
          key: "report2",
          downloadLink: downloadDealerReservationSummary,
        },
      ],
    };
  };

  const dealerBrandSettingsCheck = () => {
    // Accessories pricing isn't available to Lincoln dealers.
    if (!IS_FORD_BRAND(props.user.brand)) {
      return;
    }

    BrandSettingsClient.getDealerBrandAdjustments(props.user)
      .then((data) => {
        const labourRate = data.laborRate;
        const markup = data.markup;
        if (
          NOTNULL_OR_NOTUNDEFINED(labourRate) &&
          NOTNULL_OR_NOTUNDEFINED(markup)
        ) {
          const accessoryPricingReport = {
            key: "report5",
            name: props.t("dealerReporting.report5Name"),
            downloadLink: redirectToAccessoryPricingSearch,
          };
          setReports((prev) => [...prev, accessoryPricingReport]);
        }
      })
      .catch((error) => {
        logMsg("reason", error);
      });
  };

  useEffect(() => {
    const reportConfig = getReports();
    setReports(reportConfig.reports);
    setDisplayFilters(reportConfig.displayFilters);
    dealerBrandSettingsCheck();
  }, []);

  const handleFromDateChange = (date) => {
    if (date !== null) {
      setFromDate(date);
    }
  };

  const handleToDateChange = (date) => {
    if (date !== null) {
      setToDate(date);
    }
  };

  const handleDateRangeChange = (e) => {
    setDateRange(e.target.value);
  };

  const toggleRadio = (e) => {
    setDateRangeMethod(e.target.value);
  };

  return (
    <div id="reporting">
      <section className="pageWrapper">
        <div id="reportingHeader">
          <SubNav
            dashboard={false}
            title={props.t("Reporting.subnav1")}
            {...props}
          />
        </div>
        <div className="pagePaddingLeft pagePaddingTop">
          <InfoIconWithMessage
            message={
              displayFilters
                ? props.t("Reporting.subHeaderWithFilter")
                : props.t("Reporting.bodyCopy")
            }
          />
        </div>

        {displayFilters && (
          <div className="pagePaddingLeft pagePaddingTop">
            <ReportFilter
              dateRangeMethod={dateRangeMethod}
              dateRange={dateRange}
              dateRanges={dateRanges}
              fromDate={fromDate}
              toDate={toDate}
              toggleRadio={toggleRadio}
              handleDateRangeChange={handleDateRangeChange}
              handleFromDateChange={handleFromDateChange}
              handleToDateChange={handleToDateChange}
            />
          </div>
        )}

        <div>
          <Grid container className={styles.dataContainer}>
            <Grid
              item
              className={`${styles.headerContainer} ${styles.rowDiv} pagePaddingLeft pagePaddingRight`}
            >
              <div>{props.t("Reporting.columnTitle1")}</div>
            </Grid>
            {reports.map((report) => (
              <React.Fragment key={report.key}>
                <Grid
                  container
                  className={`${styles.rowDiv} pagePaddingLeft pagePaddingRight`}
                >
                  <Grid item md={10}>
                    <div className={styles.colValues}>
                      {props.t(report.name)}
                    </div>
                  </Grid>
                  {report.key !== "report5" && (
                    <Grid item md={2} className={`${styles.end}`}>
                      <EMPButton
                        onClick={() => report.downloadLink(report)}
                        variant="text"
                        leftIcon={<CloudDownloadIcon />}
                      >
                        {props.t("Reporting.download")}
                      </EMPButton>
                    </Grid>
                  )}
                  {report.key === "report5" && (
                    <Grid item md={2} className={`${styles.end}`}>
                      <EMPButton
                        onClick={() => report.downloadLink(report)}
                        variant="text"
                        leftIcon={<VisibilityIcon />}
                      >
                        {props.t("dealerReporting.view")}
                      </EMPButton>
                    </Grid>
                  )}
                </Grid>
              </React.Fragment>
            ))}
          </Grid>
        </div>
      </section>
    </div>
  );
};

Reporting.propTypes = {
  hideOrShow: PropTypes.func.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  t: PropTypes.func.isRequired,
  user: PropTypes.shape({
    commonId: PropTypes.string,
    dealerGroup: PropTypes.array,
    lang: PropTypes.string,
    market: PropTypes.string.isRequired,
    userType: PropTypes.string,
  }).isRequired,
  userDetails: PropTypes.shape({
    roles: PropTypes.arrayOf(PropTypes.string),
  }),
};

export default withTranslation("emp")(Reporting);
